import React, { useImperativeHandle } from 'react';

import { SvgIcon } from '@mui/material';

import { SearchType } from 'app/constants';

import useStyles from './styles';

import SearchIcon from '../../assets/search.svg';
import { useSearchField } from '../../hooks/use-search-field';
import { ChangeTrigger, SearchFieldRef, SearchFieldValue } from '../../types';
import AutoCompleteInput from '../AutoCompleteInput';
import BaseInput from '../BaseInput';
import ClearButton from '../ClearButton';

interface Props {
    className?: string;
    inputClassName?: string;
    listClassName?: string;
    value?: SearchFieldValue;
    onChange?: (value: SearchFieldValue, trigger: ChangeTrigger) => void;
    onSubmit?: () => void;
    onInputFieldEnter?: () => void;
    onOptionClick?: (option: { value: string }, event: React.MouseEvent<HTMLLIElement>) => void;
    suggestions?: { value: string }[];
    loading?: boolean;
    autoCompleteInputRef?: React.RefObject<HTMLInputElement>;
    'data-testid'?: string;
}

const MobileSearchField: React.ForwardRefRenderFunction<SearchFieldRef, Props> = (
    {
        value,
        className,
        inputClassName,
        listClassName,
        onChange,
        onInputFieldEnter,
        onSubmit,
        onOptionClick,
        suggestions,
        loading,
        autoCompleteInputRef
    },
    ref
) => {
    const { classes, cx } = useStyles();

    const {
        type,
        query,
        software,
        version,
        errors,
        canClear,
        handleReset,
        handleQueryChange,
        handleSoftwareChange,
        handleVersionChange,
        handleAutoCompleteKeyDown,
        handleSubmit
    } = useSearchField({
        value,
        onChange,
        onInputFieldEnter,
        onSubmit,
        autoCompleteInputRef
    });

    useImperativeHandle(
        ref,
        () => ({
            submit: handleSubmit
        }),
        [handleSubmit]
    );

    const searchIcon = (
        <SvgIcon className={classes.icon}>
            <SearchIcon />
        </SvgIcon>
    );

    const clearButton = canClear ? (
        <ClearButton onClick={handleReset} visible={canClear} className={classes.clear} />
    ) : null;

    return (
        <div className={cx(classes.container, className)}>
            {[SearchType.simple, SearchType.elastic].includes(type) && (
                <AutoCompleteInput
                    id={`__${type}_autocomplete_input__`}
                    ref={autoCompleteInputRef}
                    loading={loading}
                    placeholder="Search across 3M vulnerabilities"
                    className={inputClassName}
                    classes={{ root: classes.field, list: cx(classes.list, listClassName), input: classes.input }}
                    value={query}
                    onChange={handleQueryChange}
                    onKeyDown={handleAutoCompleteKeyDown}
                    options={suggestions}
                    onOptionClick={onOptionClick}
                    startAdornment={searchIcon}
                    endAdornment={clearButton}
                    data-testid={`${type}SearchInput`}
                />
            )}
            {type === SearchType.product && (
                <>
                    <AutoCompleteInput
                        id={`__${type}_autocomplete_input__`}
                        ref={autoCompleteInputRef}
                        loading={loading}
                        className={inputClassName}
                        classes={{ root: classes.field, list: cx(classes.list, listClassName), input: classes.input }}
                        value={software}
                        onChange={handleSoftwareChange}
                        onKeyDown={handleAutoCompleteKeyDown}
                        options={suggestions}
                        placeholder="Search by product"
                        onOptionClick={onOptionClick}
                        startAdornment={searchIcon}
                        endAdornment={clearButton}
                        data-testid={`${type}SearchProductInput`}
                        error={errors.software}
                    />
                    <BaseInput
                        value={version}
                        onChange={handleVersionChange}
                        onKeyDown={handleAutoCompleteKeyDown}
                        className={cx(classes.field, inputClassName)}
                        data-testid={`${type}SearchVersionInput`}
                        placeholder="Specify product version"
                        startAdornment={<span className={classes.label}>{'Ver.:'}</span>}
                        error={errors.version}
                    />
                </>
            )}
        </div>
    );
};

export default React.forwardRef(MobileSearchField);
